<template>
	<NewDesignModal class="trailer-modal trailer-modal--qtv" @closed="$emit('closed')" @dismiss="$emit('dismiss')">
		<template #title>
			<div class="qtv-modal-title">
				<span class="qtv-modal-title__name">{{ titleName }}</span>
				<span v-if="titleYear" class="qtv-modal-title__year">({{ titleYear }})</span>
			</div>
		</template>

		<div class="trailer-qtv" v-if="showQTV">
			<div class="trailer-qtv__bg">
				<PictureComp
					v-if="backdropUrl"
					imageProfile="backdrop"
					:imageUrl="backdropUrl"
					:alt="titleName"
					:lazy="false"
					fetchpriority="high"
					:object-position-top="true"
				/>
			</div>

			<div class="trailer-qtv__overlay" />

			<div class="trailer-qtv__body">
				<div class="trailer-qtv__provider">
					<ProviderIcon
						v-if="iconWideUrl"
						:iconUrl="iconWideUrl"
						:providerShortName="shortName"
						:lazy="false"
					/>
				</div>

				<p class="trailer-qtv__tagline">{{ taglineText }}</p>

				<div class="trailer-qtv__carousel-outer" :class="{ 'trailer-qtv__carousel-outer--ready': isMounted }">
					<div
						class="trailer-qtv__carousel"
						ref="carouselRef"
						@scroll.passive="onScroll"
						@touchstart="stopAutoswipe"
						@mousedown="stopAutoswipe"
						@wheel="stopAutoswipe"
					>
						<div
							v-for="(qtvTitle, index) in qtvTitles"
							:key="qtvTitle.id"
							class="trailer-qtv__item-wrap"
							:style="itemStyles[index]"
							v-observe-visibility="posterObservationOptions[index]"
						>
							<ClickoutAnchor
								:href="clickoutUrls[index]"
								rel="nofollow noopener"
								class="trailer-qtv__item"
							>
								<img
									:src="getPosterUrl(qtvTitle)"
									:alt="qtvTitle.content.title"
									class="trailer-qtv__poster"
								/>
								<div class="trailer-qtv__poster-fade" />
								<div class="trailer-qtv__poster-meta">
									<img src="/appassets/img/imdb.png" class="trailer-qtv__imdb-logo" alt="IMDB" />
									<span v-if="getImdbScore(qtvTitle)" class="trailer-qtv__imdb-score">{{
										getImdbScore(qtvTitle)
									}}</span>
								</div>
							</ClickoutAnchor>
						</div>
					</div>
				</div>

				<ClickoutAnchor
					:href="ctaClickoutUrl"
					rel="nofollow noopener"
					class="trailer-qtv__cta"
					v-observe-visibility="ctaObservationOptions"
				>
					<FontAwesomeIcon :icon="faPlay" class="trailer-qtv__cta-icon" />
					{{ $tc('WEBAPP_STREAM_FREE') }}
				</ClickoutAnchor>

				<p v-if="priceText" class="trailer-qtv__price">{{ priceText }}</p>
			</div>

			<footer class="trailer-qtv__footer">
				<span class="trailer-qtv__ad-label">{{ adLabel }}</span>
				<button class="trailer-qtv__skip-btn" :disabled="skipCountdown > 0" @click="handleSkip">
					<span v-if="skipCountdown > 0">{{ skipInText }}</span>
					<span v-else>{{ skipLabel }}</span>
					<FontAwesomeIcon :icon="faForwardStep" size="xs" />
				</button>
			</footer>
		</div>

		<div class="player-container" v-else>
			<component
				v-if="trailerClip"
				:is="videoPlayer"
				:additionalContexts="playerAdditionalContexts"
				class="player"
				:class="{ 'youtube-player-active': isYoutubePlayer }"
				height="100%"
				:clip="trailerClip"
				:options="playerOptions"
				:isBackdrop="false"
				:isActive="true"
				:title="title"
				:hideTrailerAd="hideTrailerAd"
				@onBuffering="onTrack('buffering')"
				@onPlaying="onTrack('playing')"
				@onPaused="onTrack('paused')"
				@onEnded="onTrack('ended')"
			/>
			<JWTVProFooter v-if="showJWTVProFooter" :title="title" />
		</div>
	</NewDesignModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { usePointerSwipe } from '@vueuse/core';
import { SnowplowTitleContextGraphql, type SnowplowContext } from '@/helpers/tracking/providers';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { QTVSimilarTitle, useQTV } from '@/features/buybox/composables/useQTV';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import NewDesignModal from '@/components/modals/vfm/NewDesignModal.vue';
import { getThumborUrl } from '@/filters/thumbor';
import { faPlay, faForwardStep } from '@fortawesome/free-solid-svg-icons';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { OfferLocation } from '@/helpers/offer-helper';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { getTrailerComponent } from '@/helpers/trailer-helper';
import type { TrailerClip } from '@/interfaces/trailerClip';
import type { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { isProWebCountry } from '@/helpers/pro-web-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

const JWTVProFooter = () => import('@/pages/titleDetailV1/v1/hero/JWTVProFooter.vue');

const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		clips: TrailerClip[];
		shortName: string;
		additionalContexts?: SnowplowContext[];
		playerAdditionalContexts?: SnowplowContext[];
		hideTrailerAd?: boolean;
		source?: string;
		trackingProperty?: string;
	}>(),
	{
		additionalContexts: () => [],
		source: 'modal',
	}
);

defineEmits<{ (e: 'closed'): void; (e: 'dismiss'): void }>();

const { country, webLocale } = storeToRefs(useLanguageStore());
const { isPremium } = storeToRefs(useUserStore());
const { titleObjectType } = useTitleDetailInfo(() => props.title);

const { onIntersection } = useOfferImpressionTracking({ groupId: 'trailer_modal_qtv' });
const { onIntersection: onIntersectionCTA } = useOfferImpressionTracking({ groupId: 'trailer_modal_qtv_cta' });

const { qtvTitles } = useQTV({
	shortName: () => props.shortName,
	sourceTitle: () => props.title,
	titlesShownCount: 5,
	additionalContexts: () => props.additionalContexts,
	titleCount: 11,
});

const { promotionOffer: fallbackPromotionOffer } = usePromotion({
	title: () => props.title,
	offers: () => [],
	hardcodedShortName: () => props.shortName,
});

const carouselRef = ref<HTMLElement | null>(null);
const isMounted = ref(false);
const isMobile = ref(false);
const scrollIndex = ref(0);
const skipCountdown = ref(5);
const scrollCentered = ref(false);
const debounceRAFId = ref<number | null>(null);
const autoswipeRAFId = ref<number | null>(null);
const mq = ref<MediaQueryList | null>(null);
const countdownInterval = ref<ReturnType<typeof setInterval> | undefined>(undefined);
const autoswipeInterval = ref<ReturnType<typeof setInterval> | undefined>(undefined);
const showTrailer = ref(false);
const showQTV = computed(() => !showTrailer.value && qtvTitles.value.length > 0);

const playerOptions: YoutubePlayerOptions = { autoplay: 1, mute: 1 };

const adLabel = 'Ad';
const skipLabel = 'Skip';
const skipInText = computed(() => `Skip in ${skipCountdown.value}s`);

const titleName = computed(() => props.title.content.title);
const titleYear = computed(() => props.title?.content?.originalReleaseYear);
const taglineText = computed(() => `Streamers who watched ${titleName.value} also watched...`);
const backdropUrl = computed(() => props.title?.content?.backdrops?.[0]?.backdropUrl ?? '');
const iconWideUrl = computed(() => qtvTitles.value[0]?.offers?.[0]?.package?.iconWide ?? null);

const priceText = computed(() => {
	const planOffers =
		qtvTitles.value[0]?.offers?.find(o => o.package.shortName === props.shortName)?.package?.planOffers ?? [];
	if (!planOffers.length) return null;
	const plan = planOffers.find(p => p.isTrial || p.children.length > 0);
	if (!plan) return null;
	const planWithTrial = plan.children.length > 0 ? { ...plan.children[0], retailPrice: plan.retailPrice } : plan;
	if (!planWithTrial.retailPrice) return null;

	return `${planWithTrial.durationDays} Days Free, Then ${planWithTrial.retailPrice} / month`;
});

const clickoutUrls = computed(() =>
	qtvTitles.value.map(qtvTitle =>
		new ClickoutUrl(qtvTitle.offers[0])
			.set('uct_country', country.value)
			.addContexts(props.additionalContexts)
			.setTitleSourceId(qtvTitle.id)
			.addContexts(getTitleContext(qtvTitle))
			.setForTracking('offerLocation', OfferLocation.BACKDROP)
			.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
			.updateContext('ClickoutContextGraphql', 'clickoutType', 'poster')
	)
);

const ctaClickoutUrl = computed(() =>
	new ClickoutUrl(fallbackPromotionOffer.value)
		.set('uct_country', country.value)
		.addContexts(props.additionalContexts)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'free_trial')
		.setTitleSourceId(props.title.id)
);

const posterObservationOptions = computed(() =>
	qtvTitles.value.map((_, index) => ({
		...observableOptions,
		callback: (isVisible: boolean) => {
			if (!isVisible) return;
			onIntersection(clickoutUrls.value[index], isVisible);
		},
	}))
);

const ctaObservationOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => {
		if (!isVisible) return;
		onIntersectionCTA(ctaClickoutUrl.value, isVisible);
	},
}));

const itemStyles = computed(() => {
	return qtvTitles.value.map((_, index) => {
		const offset = index - scrollIndex.value;
		const absOffset = Math.abs(offset);
		return {
			'--offset': offset,
			'--abs-offset': absOffset,
			'--opacity': Math.min(1, Math.max(0, 3 - Math.round(absOffset))),
			'--scale-origin': scaleOrigin(offset, absOffset),
			'--z-index': Math.max(0, 10 - Math.abs(index - Math.round(scrollIndex.value))),
		};
	});
});

const trailerClip = computed(() => props.clips[0]);
const isYoutubePlayer = computed(() => trailerClip.value?.provider === 'YOUTUBE');
const videoPlayer = computed(() => getTrailerComponent(trailerClip.value?.provider));
const showJWTVProFooter = computed(
	() => !isPremium.value && isProWebCountry(webLocale.value) && trailerClip.value?.provider === 'JUSTWATCH_TV'
);

const { isSwiping } = usePointerSwipe(carouselRef);

function handleSkip() {
	showTrailer.value = true;

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'trailer_qtv_skipped',
			label: props.title.id,
			property: 'title-header',
		},
		props.additionalContexts
	);
}

function onScroll() {
	if (debounceRAFId.value !== null) return;
	debounceRAFId.value = requestAnimationFrame(() => {
		debounceRAFId.value = null;
		const el = carouselRef.value;
		if (!el) return;
		const center = el.scrollLeft + el.clientWidth / 2;
		const items = Array.from(el.children) as HTMLElement[];
		if (!items.length) return;
		const centers = items.map(item => item.offsetLeft + item.offsetWidth / 2);

		const next = centers.findIndex(c => c > center);
		if (next === -1) {
			scrollIndex.value = centers.length - 1;
			return;
		}
		if (next === 0) {
			scrollIndex.value = 0;
			return;
		}
		scrollIndex.value = next - 1 + (center - centers[next - 1]) / (centers[next] - centers[next - 1]);
	});
}

function scrollToIndex(index: number, duration = 1200) {
	const el = carouselRef.value;
	if (!el) return;
	const items = Array.from(el.children) as HTMLElement[];
	const item = items[index];
	if (!item) return;
	const snapTarget = item.offsetLeft + item.offsetWidth / 2 - el.clientWidth / 2;
	const start = el.scrollLeft;
	const diff = snapTarget - start;
	if (!diff) return;
	const startTime = performance.now();
	const scrollEl = el;
	scrollEl.style.scrollSnapType = 'none';
	function step(now: number) {
		const t = Math.min((now - startTime) / duration, 1);
		const ease = 1 - (1 - t) * (1 - t);
		scrollEl.scrollLeft = start + diff * ease;
		if (t < 1) {
			autoswipeRAFId.value = requestAnimationFrame(step);
		} else {
			autoswipeRAFId.value = null;
			scrollEl.style.scrollSnapType = '';
		}
	}
	autoswipeRAFId.value = requestAnimationFrame(step);
}

function stopAutoswipe() {
	clearInterval(autoswipeInterval.value);
	if (autoswipeRAFId.value !== null) {
		cancelAnimationFrame(autoswipeRAFId.value);
		autoswipeRAFId.value = null;
		if (carouselRef.value) carouselRef.value.style.scrollSnapType = '';
	}
}

function startAutoswipe() {
	clearInterval(autoswipeInterval.value);
	autoswipeInterval.value = setInterval(() => {
		const qtvCount = qtvTitles.value.length;
		if (!qtvCount) return;
		const current = Math.round(scrollIndex.value);
		const nextIndex = (current + 1) % qtvCount;
		if (nextIndex === 0) {
			const el = carouselRef.value;
			if (!el) return;
			const item = el.children[0] as HTMLElement;
			if (item) el.scrollLeft = item.offsetLeft + item.offsetWidth / 2 - el.clientWidth / 2;
		} else {
			scrollToIndex(nextIndex);
		}
	}, 3500);
}

function scaleOrigin(offset: number, absOffset: number): string {
	const rounded = Math.round(absOffset);
	if (rounded === 0) return '50% center';
	const toRight = offset > 0;

	if (isMobile.value) {
		const pct = rounded >= 2 ? 230 : 150;
		return toRight ? `-${pct}% center` : `${100 + pct}% center`;
	}
	return rounded >= 2 ? (toRight ? '-20% center' : '120% center') : '50% center';
}

function getPosterUrl(title: QTVSimilarTitle) {
	return (
		getThumborUrl({
			url: title.content.posterUrl,
			imageType: 'poster',
		}).replace('{format}', 'jpg') ?? undefined
	);
}

function getImdbScore(title: QTVSimilarTitle) {
	return title.content.scoring?.imdbScore?.toFixed(1);
}

function getTitleContext(qtvTitle: QTVSimilarTitle) {
	const index = qtvTitles.value.findIndex(title => title.id === qtvTitle.id);
	const indexForTitleContext = index > -1 ? index + 1 : undefined;

	return new SnowplowTitleContextGraphql(
		qtvTitle.objectId,
		qtvTitle.objectType,
		undefined,
		null,
		undefined,
		indexForTitleContext
	);
}

function onTrack(eventName: string) {
	if (trailerClip.value?.provider !== 'YOUTUBE') return;
	const trackingMap = {
		buffering: 'youtube_buffering',
		playing: 'youtube_playing',
		paused: 'youtube_paused',
		ended: 'youtube_ended',
	};
	const category = trackingMap[eventName as keyof typeof trackingMap];
	if (category) {
		TrackingHelper.trackEvent(
			category,
			{
				action: titleObjectType.value.toLowerCase(),
				label: props.source,
				property: props.trackingProperty,
				nonInteraction: true,
				value: 1,
			},
			props.additionalContexts
		);
	}
}

function mqHandler(e: MediaQueryListEvent) {
	isMobile.value = e.matches;
}
watch(isSwiping, isCurrentlySwiping => {
	if (!isCurrentlySwiping) return;
	stopAutoswipe();
	carouselRef.value?.addEventListener(
		'click',
		e => {
			e.preventDefault();
			e.stopPropagation();
		},
		{ once: true, capture: true }
	);
});

watch(
	qtvTitles,
	titles => {
		if (!titles.length || scrollCentered.value) return;
		scrollCentered.value = true;

		const midIndex = Math.floor(titles.length / 2);
		scrollIndex.value = midIndex;
		requestAnimationFrame(() => {
			const el = carouselRef.value;
			if (!el) return;
			const items = Array.from(el.children) as HTMLElement[];
			const item = items[midIndex];
			if (item) el.scrollLeft = item.offsetLeft + item.offsetWidth / 2 - el.clientWidth / 2;
		});
	},
	{ immediate: true }
);

onMounted(() => {
	isMounted.value = true;
	countdownInterval.value = setInterval(() => {
		skipCountdown.value--;
		if (skipCountdown.value <= 0) clearInterval(countdownInterval.value);
	}, 1000);

	mq.value = window.matchMedia('(max-width: 800px)');
	isMobile.value = mq.value.matches;
	mq.value.addEventListener('change', mqHandler);

	startAutoswipe();
});

onBeforeUnmount(() => {
	clearInterval(countdownInterval.value);
	stopAutoswipe();
	mq.value?.removeEventListener('change', mqHandler);
});
</script>

<style scoped lang="scss">
@import '~@/styles/variables.scss';

::v-deep(.trailer-modal--qtv.jw-modal-new) {
	background-color: #1a2029;

	.jw-modal-new__header--icons--left {
		display: none;
	}

	.jw-modal-new__body {
		overflow: hidden;
	}

	@media (max-width: 750px) {
		header {
			padding: 8px;
		}
	}

	.video-player {
		max-height: unset;
	}
}

.qtv-modal-title {
	&__name {
		color: #fff;
		font-size: 18px;
		font-weight: 700;
	}

	&__year {
		margin-left: 6px;
		color: rgba(255, 255, 255, 0.5);
		font-size: 18px;
		font-weight: 400;
	}
}

.trailer-qtv {
	--item-w: 150px;
	--item-h: 225px;
	--scale-offset: 0.15;
	--depth-offset: -50px;

	@media (max-width: 800px) {
		--scale-offset: 0.2;
		--depth-offset: -40px;
	}

	@media (max-width: 520px) {
		--scale-offset: 0.22;
	}

	@media (max-width: 450px) {
		--item-w: 120px;
		--item-h: 180px;
		--scale-offset: 0.24;
	}
	@media (max-width: 400px) {
		--scale-offset: 0.25;
	}

	position: relative;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: clip;

	&__bg {
		position: absolute;
		inset: 0;
		z-index: 0;

		:deep(.picture-comp) {
			width: 100%;
			height: 100%;
		}

		:deep(.picture-comp__img) {
			width: 100%;
			height: 100%;
			object-fit: cover;
			object-position: center top;
		}
	}

	&__overlay {
		position: absolute;
		inset: 0;
		background: rgba(12, 19, 30, 0.72);
		pointer-events: none;
		z-index: 1;
	}

	&__body {
		position: relative;
		z-index: 2;
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 20px;
		padding: 24px 0;
		min-height: 0;

		@media (max-width: 800px) {
			gap: 16px;
		}
	}

	&__provider {
		display: flex;
		justify-content: center;

		:deep(.provider-icon) {
			height: 36px;
			width: auto;
		}
	}

	&__tagline {
		color: #fff;
		font-size: 16px;
		line-height: 24px;
		letter-spacing: 0.02em;
		font-weight: 900;
		text-align: center;
		margin: 0;
		padding: 0 32px;

		strong {
			font-weight: 700;
		}
	}

	&__carousel-outer {
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 14px;
		visibility: hidden;

		&--ready {
			visibility: visible;
		}
	}

	&__carousel {
		display: flex;
		align-items: center;
		gap: 6px;
		overflow-x: scroll;
		scroll-snap-type: x mandatory;
		touch-action: pan-x;
		scrollbar-width: none;
		-webkit-overflow-scrolling: touch;
		width: 100%;
		padding-inline: calc(50% - var(--item-w) / 2);
		padding-block: 8px;
		box-sizing: border-box;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	&__item-wrap {
		scroll-snap-align: center;
		scroll-snap-stop: always;
		flex-shrink: 0;
		perspective: 600px;
		z-index: var(--z-index, 0);
		position: relative;
	}

	&__item {
		display: block;
		position: relative;
		width: var(--item-w);
		height: var(--item-h);
		border-radius: 8px;
		overflow: hidden;
		cursor: pointer;

		transform-origin: var(--scale-origin, 50% center);
		transform: translateZ(calc(var(--abs-offset) * var(--depth-offset)))
			scale(calc(1 - var(--abs-offset) * var(--scale-offset)));
		opacity: var(--opacity, 1);

		transition: transform 0.4s cubic-bezier(0.35, 0.56, 0.85, 0.94), opacity 0.45s ease,
			transform-origin 0.5s cubic-bezier(0.45, 0.5, 0.8, 0.94), z-index 0.6s ease;
	}

	&__poster {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__poster-fade {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 60px;
		background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 100%);
		pointer-events: none;
	}

	&__poster-meta {
		position: absolute;
		bottom: 4px;
		left: 50%;
		transform: translateX(-50%);
		display: flex;
		align-items: center;
		gap: 4px;
		justify-content: center;
	}

	&__imdb-logo {
		width: 20px;
		height: auto;
	}

	&__imdb-score {
		color: #78a6b8;
		font-size: 12px;
		font-weight: 400;
	}

	&__cta {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		background: #fbc500;
		color: #000;
		font-size: 16px;
		font-weight: 900;
		padding: 9px 24px;
		border-radius: 8px;
		cursor: pointer;
		min-width: 160px;
		white-space: nowrap;

		&:hover {
			background: rgba(251, 197, 0, 0.85);
		}
	}

	&__cta-icon {
		font-size: 16px;
	}

	&__price {
		color: var(--jw-text-medium);
		font-size: 12px;
		margin: 0;
		text-align: center;
	}

	&__footer {
		position: relative;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 24px;
		background-color: #1a2029;

		@media (max-width: 800px) {
			padding: 10px 24px;
		}
	}

	&__ad-label {
		color: var(--jw-text-medium);
		font-size: 14px;
		letter-spacing: 0.5px;
		font-weight: 900;
		line-height: 16px;
	}

	&__skip-btn {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: none;
		border: none;
		border-radius: 4px;
		color: var(--jw-text-medium);
		font-size: 14px;
		font-weight: 900;
		line-height: 16px;
		padding: 6px 10px;

		&:not(:disabled) {
			border: 1px solid rgba(255, 255, 255, 0.35);
			cursor: pointer;

			&:hover {
				color: var(--jw-text-high);
				border-color: rgba(255, 255, 255, 0.75);
			}
		}
	}
}

.player-container {
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	background-color: black;
}

.player {
	height: 100%;
	width: 100%;
	padding: 20px;
	max-height: 100% !important;

	&.youtube-player-active {
		::v-deep(.video-player__video-container) {
			height: 100%;
		}
	}
}
</style>
